import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ArrowLeft, Mail, MessageSquareText, Phone, Trash2, UserRound } from "lucide-react";
import "../../assets/Styles/CustomerService.css";
import Loader from "../../Loader/Loader";
import ReplyPopup from "../../components/common/Reply";
import DeletePopup from "../../components/common/Aresure";
import { Snackbar } from "../../utils/snackbar";
import { customerservicedetails, deletecustomerservice, serviceaction } from "../../services/Mainservice";

const CustomerServiceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [notice, setNotice] = useState({ open: false, message: "", severity: "success" });

  const handleError = (error) => {
    if (error?.status === 401 || error?.code === 401) {
      localStorage.clear();
      navigate("/");
      return;
    }
    setNotice({ open: true, message: error?.message || "Something went wrong", severity: "error" });
  };

  const loadTicket = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      const response = await customerservicedetails(Number(id));
      setTicket(response.data.data);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) loadTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleReply = async (reply) => {
    try {
      setSubmitting(true);
      const response = await serviceaction(Number(id), reply);
      setReplyOpen(false);
      setNotice({ open: true, message: response.data.message || "Reply sent successfully", severity: "success" });
      await loadTicket(false);
      return true;
    } catch (error) {
      handleError(error);
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      setSubmitting(true);
      await deletecustomerservice(Number(id));
      navigate("/Customer_Service", { replace: true });
    } catch (error) {
      handleError(error);
      setSubmitting(false);
    }
  };

  if (loading) return <Loader />;

  if (!ticket) {
    return <div className="customer-detail-shell customer-service-empty"><p>Ticket details could not be loaded.</p><button className="customer-secondary-button" onClick={() => navigate("/Customer_Service")}>Back to tickets</button></div>;
  }

  const profile = ticket.user || {};
  const name = ticket.FullName || ticket.FirstName || ticket.name || "Guest";
  const email = ticket.userEmail || ticket.email || profile.email;
  const phone = ticket.phoneNumber || profile.mobilenumber;

  return (
    <>
      <div className="customer-detail-shell">
        <button className="customer-back-button" type="button" onClick={() => navigate("/Customer_Service")}><ArrowLeft size={18} /> Back to tickets</button>
        <div className="customer-detail-heading">
          <div><p className="customer-detail-eyebrow">Support ticket #{ticket.id}</p><h1>{ticket.subject || "No subject"}</h1><p>{ticket.formattedDate || ticket.dateTime || ticket.createdAt}</p></div>
          <span className={ticket.isReply ? "complete_data" : "cancel_data"}>{ticket.isReply ? "Replied" : "Unanswered"}</span>
        </div>

        <div className="customer-detail-grid">
          <section className="customer-detail-card customer-message-card"><h2>Customer message</h2><p>{ticket.Description || ticket.message || "No message provided."}</p></section>
          <aside className="customer-detail-card">
            <div className="customer-profile-title">
              {ticket.image || profile.image ? <img src={ticket.image || profile.image} alt="" /> : <span className="customer-avatar large">{name.charAt(0).toUpperCase()}</span>}
              <div><h2>{name}</h2><small>{ticket.userId ? "Registered user" : "Guest submitter"}</small></div>
            </div>
            <div className="customer-profile-list">
              <p><Mail size={17} /><span>{email || "No email"}</span></p>
              <p><Phone size={17} /><span>{phone || "No phone number"}</span></p>
              <p><UserRound size={17} /><span>{profile.gender || "Gender not provided"}</span></p>
              {profile.city && <p><span>City</span><strong>{profile.city}</strong></p>}
              {profile.dob && <p><span>Date of birth</span><strong>{profile.dob}</strong></p>}
              {ticket.userId && <p><span>Account</span><strong>{profile.isBlock ? "Blocked" : profile.isSuspend ? "Suspended" : "Active"}</strong></p>}
            </div>
          </aside>
        </div>

        <div className="customer-detail-actions">
          {!ticket.isReply && <button className="customer-primary-button" type="button" onClick={() => setReplyOpen(true)}><MessageSquareText size={18} /> Reply</button>}
          <button className="customer-danger-button" type="button" onClick={() => setDeleteOpen(true)}><Trash2 size={18} /> Delete ticket</button>
        </div>
      </div>

      <ReplyPopup key={id} isOpen={replyOpen} setisOpen={setReplyOpen} handleReply={handleReply} onClose={() => setReplyOpen(false)} ticketId={id} isSubmitting={submitting} />
      <DeletePopup isOpen={deleteOpen} setisOpen={setDeleteOpen} handleDelete={handleDelete} onClose={() => setDeleteOpen(false)} />
      <Snackbar open={notice.open} message={notice.message} severity={notice.severity} onclose={() => setNotice((value) => ({ ...value, open: false }))} />
    </>
  );
};

export default CustomerServiceDetails;
